import { useState, useEffect, useRef } from "react";
import { Match } from "../types";
import { formatMatchClock } from "../utils";
import { updateMatchStatus, updateMatch } from "../../../lib/loggerApi";

/**
 * Safely parse a timestamp string to milliseconds.
 * Handles both ISO timestamps with 'Z' suffix and naive timestamps (treated as UTC).
 */
export const parseTimestamp = (
  timestamp: string | undefined | null,
): number => {
  if (!timestamp) return 0;
  // If timestamp doesn't end with Z and doesn't have timezone info, treat as UTC
  const normalized =
    timestamp.endsWith("Z") ||
    timestamp.includes("+") ||
    timestamp.includes("-", 10)
      ? timestamp
      : `${timestamp}Z`;
  return new Date(normalized).getTime();
};

/**
 * Floor a clock value the client is about to persist at whatever the match
 * document already carries.
 *
 * Effective and ineffective time only move forward within a match, so the
 * stored value is a hard lower bound. The values the handlers below persist
 * come from `currentEffectiveRef` / `currentIneffectiveRef`, which are
 * `useRef(0)` and are only filled in by the 100ms timer effect — a handler
 * that fires while those refs are stale (or before the first tick) used to
 * persist 0 verbatim. On 2026-08-18 that wiped seven minutes off a live
 * match: 461s stored, 0 written, and every event afterwards stamped from the
 * restarted clock.
 *
 * The server enforces the same rule (matches_new.MONOTONIC_CLOCK_FIELDS);
 * this is the client half of that guard, so a stale ref never even leaves the
 * browser. Deliberate rewinds — only `handleGlobalClockReset` — bypass both
 * by sending `allow_clock_rewind`.
 */
/** Live clock values as of the last 100ms tick. See `clockSnapshotRef`. */
export interface MatchClockSnapshot {
  effectiveSeconds: number;
  ineffectiveSeconds: number;
  varSeconds: number;
}

export const monotonicClockValue = (
  candidate: number,
  stored: number | null | undefined,
): number => {
  const flooredCandidate = Number.isFinite(candidate)
    ? Math.max(0, Math.round(candidate))
    : 0;
  const flooredStored =
    typeof stored === "number" && Number.isFinite(stored)
      ? Math.max(0, Math.round(stored))
      : 0;
  return Math.max(flooredCandidate, flooredStored);
};

/**
 * A mode-switch PATCH gets a small bounded number of attempts before it is
 * treated as failed. See PMA-COCK-155: the prod incident's dropped PATCH was
 * a single unretried request lost to a transient iPad network failure.
 */
const MODE_SWITCH_MAX_ATTEMPTS = 3;
const MODE_SWITCH_RETRY_BASE_MS = 300;

/**
 * PMA-COCK-003 v1.182.0: narrows `clockActionError` to the server's
 * deliberate refusal — any thrown error with `status === 409` — instead of
 * every thrown error.
 *
 * v1.179.0 set `clockActionError` on ANY throw from either
 * `updateMatchStatus` or `updateMatch` in `handleGlobalClockStop` /
 * `handleGlobalClockReset` — that was the fix for the real bug (the refusal
 * used to be swallowed by a console.error-only catch, see those handlers'
 * `finally` blocks), but it overshot: an ordinary transient network failure
 * or an offline blip now also raised an operator-facing toast that never
 * existed before this UC.
 *
 * A 409 from either call is treated identically here, deliberately: today
 * that means TWO distinct server refusals, not one — `updateMatch`'s
 * `/clock-mode` PATCH refuses with 409 while `match.status === "Penalties"`
 * (PMA-MATCH-030, "the shootout freezes everything"), and `updateMatchStatus`'s
 * `/status` PATCH separately refuses with 409 on "Match status changed
 * concurrently; please retry" (an unrelated optimistic-concurrency conflict —
 * `update_match_status`'s own `already_in_penalties` branch is a silent
 * no-op, never a 409, so this is NOT the Penalties case wearing a different
 * message). Both are the server actively refusing the request, not a
 * transient/offline failure, and the toast copy
 * (`clockActionFailedToast`, "the clock update did not save... check the
 * match state") reads correctly for either — so both toast, and neither is
 * special-cased apart from the other.
 *
 * `loggerApi.ts`'s `updateMatchStatus`/`updateMatch` attach `error.status`
 * to the thrown `Error` (v1.182.0, mirroring `getMatch`'s pre-existing
 * pattern) specifically so this check does not have to regex the response
 * body embedded in `error.message` — a fragile detector would be worse than
 * the over-broad toast it replaces.
 */
const isClockActionRefusal = (error: unknown): boolean =>
  error instanceof Error &&
  (error as Error & { status?: number }).status === 409;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useMatchTimer = (
  match: Match | null,
  fetchMatch: () => void,
  options?: {
    varTimeSeconds?: number;
    isVarActive?: boolean;
    varStartMs?: number | null;
    varPauseStartMs?: number | null;
    varPausedSeconds?: number;
    timeoutSeconds?: number;
    isTimeoutActive?: boolean;
  },
) => {
  const [globalClock, setGlobalClock] = useState("00:00");
  const [effectiveTime, setEffectiveTime] = useState(0);
  const [effectiveClock, setEffectiveClock] = useState("00:00");
  const [ineffectiveClock, setIneffectiveClock] = useState("00:00");

  const [clockMode, setClockMode] = useState<"EFFECTIVE" | "INEFFECTIVE">(
    "EFFECTIVE",
  );
  const [accumulatedIneffectiveTime, setAccumulatedIneffectiveTime] =
    useState(0);
  const [isClockRunning, setClockRunning] = useState(false);
  /**
   * Set when a mode-switch PATCH exhausts every retry attempt. `null` means
   * no failure is currently outstanding — cleared at the start of the next
   * attempt. Consumers outside this hook (e.g. a toast) read this to tell
   * the operator the clock mode they see may not be what the server has;
   * `fetchMatch()` is also fired on exhaustion so `match` re-syncs to
   * whatever the server actually has as soon as this fires.
   */
  const [modeSwitchError, setModeSwitchError] = useState<string | null>(null);
  /**
   * PMA-COCK-003 v1.179.0, narrowed v1.182.0: set when `handleGlobalClockStop`
   * or `handleGlobalClockReset`'s server round-trip throws the server's
   * DELIBERATE refusal — any thrown error with `status === 409`, detected via
   * `isClockActionRefusal` — not every thrown error. Today this covers two
   * distinct refusals: the `PATCH .../clock-mode` 409 while the match is in
   * `Penalties` (PMA-MATCH-030 v1.9.3, "the shootout freezes everything") and
   * the `PATCH .../status` 409 on a concurrent-modification conflict; see
   * `isClockActionRefusal`'s own doc comment for why both toast identically
   * and why v1.179.0 overshot. A generic/transient failure (network blip,
   * offline) is still `console.error`-ed in the `catch` but does NOT set this
   * field. Mirrors `modeSwitchError` below
   * (same one-shot null-at-the-start-of-every-attempt contract, same "do not
   * leave the operator silently guessing" reasoning) but is a separate
   * field: these two handlers have no retry loop and no optimistic local
   * state to revert, so `modeSwitchError`'s own "after repeated attempts"
   * phrasing would be inaccurate for them. `fetchMatch()` always runs
   * regardless of this value — see both handlers' `finally` blocks — this
   * field is purely for telling the operator the server refused the
   * request.
   */
  const [clockActionError, setClockActionError] = useState<string | null>(null);

  // Track current calculated values for mode switch calculations
  const currentEffectiveRef = useRef(0);
  const currentIneffectiveRef = useRef(0);
  const currentVarRef = useRef(0);
  /**
   * The same three values, exposed to callers as one ref.
   *
   * `handleModeSwitch` below persists derived clock values to the server, but
   * the local `match` object only catches up a network round-trip later. Any
   * caller that updates `match` optimistically alongside a mode switch has to
   * write the SAME values, or the 100ms timer loop recomputes the clocks from
   * a half-updated match and the display jumps. See `optimisticModeChange` in
   * `useIneffectiveTime.ts`.
   */
  const clockSnapshotRef = useRef<MatchClockSnapshot>({
    effectiveSeconds: 0,
    ineffectiveSeconds: 0,
    varSeconds: 0,
  });
  const isVarActive = options?.isVarActive ?? false;
  const varStartMs = options?.varStartMs ?? null;
  const varPauseStartMs = options?.varPauseStartMs ?? null;
  const varPausedSeconds = options?.varPausedSeconds ?? 0;
  const timeoutSeconds = options?.timeoutSeconds ?? 0;
  const isTimeoutActive = options?.isTimeoutActive ?? false;

  // Sync state with match data
  useEffect(() => {
    if (match) {
      setAccumulatedIneffectiveTime(match.ineffective_time_seconds || 0);
      const nextMode =
        match.clock_mode === "INEFFECTIVE" ? "INEFFECTIVE" : "EFFECTIVE";
      setClockMode(nextMode);
    }
  }, [match]);

  // Timer Loop
  useEffect(() => {
    if (!match) return;

    const updateTimers = () => {
      const now = Date.now();

      const isStatusStopped =
        match.status === "Halftime" ||
        match.status === "Extra_Halftime" ||
        match.status === "Fulltime" ||
        match.status === "Completed" ||
        match.status === "Abandoned";

      // 1. Calculate Sub-Clocks based on Mode
      let currentEffectiveSeconds = match.match_time_seconds || 0;
      // If clock is stopped (no current_period_start), use the stored time
      if (!match.current_period_start_timestamp) {
        currentEffectiveSeconds =
          match.clock_seconds_at_period_start || match.match_time_seconds || 0;
      }

      let currentIneffectiveSeconds = accumulatedIneffectiveTime;
      let currentVarSeconds = 0;

      // If global clock is running (according to backend)
      if (match.current_period_start_timestamp && !isStatusStopped) {
        const start = parseTimestamp(match.current_period_start_timestamp);
        const elapsed = Math.max(0, (now - start) / 1000);
        const varPauseDelta =
          varPauseStartMs && isVarActive
            ? Math.max(0, (now - varPauseStartMs) / 1000)
            : 0;
        const varPauseTotal = Math.max(0, varPausedSeconds + varPauseDelta);

        // Add elapsed to the active mode accumulator
        // IMPORTANT: Only accumulate if the backend match state matches our local optimistic clockMode.
        // Otherwise, we might calculate elapsed time using a timestamp from the WRONG mode (e.g. previous mode start),
        // leading to massive time jumps (phantom accumulation) which can be permanently saved if a save triggers during the glitch.
        if (clockMode === "EFFECTIVE" && !isTimeoutActive) {
          if (isVarActive && varStartMs !== null) {
            // VAR is active: freeze effective at the value it had when VAR was
            // pressed (baseline + pre-VAR elapsed).  Using baseline alone would
            // cause a backward jump of D seconds if the clock ran D seconds
            // before VAR was toggled.
            // `start` = parseTimestamp(current_period_start_timestamp) — computed above.
            const preVarElapsed = Math.max(0, (varStartMs - start) / 1000);
            currentEffectiveSeconds =
              (match.clock_seconds_at_period_start || 0) + preVarElapsed;
          } else if (isVarActive) {
            // varStartMs unavailable (rehydration from breakdownVarActive):
            // freeze at stored value — we cannot reconstruct D without the press timestamp.
            currentEffectiveSeconds =
              match.clock_seconds_at_period_start ||
              match.match_time_seconds ||
              0;
          } else if (match.clock_mode === "EFFECTIVE") {
            currentEffectiveSeconds =
              (match.clock_seconds_at_period_start || 0) +
              Math.max(0, elapsed - varPauseTotal);
          } else {
            currentEffectiveSeconds =
              match.clock_seconds_at_period_start ||
              match.match_time_seconds ||
              0;
          }
        } else if (clockMode === "INEFFECTIVE") {
          // Ineffective time always accumulates when in INEFFECTIVE mode,
          // regardless of isTimeoutActive — a timeout cannot logically be
          // active during an ineffective period (e.g. offside).
          if (
            match.clock_mode === "INEFFECTIVE" &&
            match.last_mode_change_timestamp
          ) {
            const lastChange = parseTimestamp(match.last_mode_change_timestamp);
            const elapsedSinceChange = Math.max(0, (now - lastChange) / 1000);
            currentIneffectiveSeconds += Math.max(
              0,
              elapsedSinceChange - varPauseTotal,
            );
          }
        }

        // VAR seconds: ticks independently while effective is frozen.
        // Only computed in EFFECTIVE clockMode to avoid double-counting with the
        // ineffective accumulator (which already covers VAR when clockMode is INEFFECTIVE).
        if (isVarActive && varStartMs !== null && clockMode === "EFFECTIVE") {
          const varElapsed = Math.max(0, (now - varStartMs) / 1000);
          currentVarSeconds = Math.max(0, varElapsed - varPauseTotal);
        }

        setClockRunning(true);
      } else {
        setClockRunning(false);
      }

      // 2. Calculate Global Time (effective + ineffective + VAR)
      // Effective is frozen during VAR; currentVarSeconds keeps the wall clock
      // advancing so the total (global) clock never freezes during a VAR review
      // (a deliberate live-display choice, unrelated to PMA-COCK-131 and not
      // touched by it). timeoutSeconds is NOT added here — PMA-COCK-131:
      // handleTimeoutToggle now switches clock_mode to INEFFECTIVE for a
      // timeout's entire duration, so currentIneffectiveSeconds already
      // contains it once clock_mode catches up (see the clockMode==="INEFFECTIVE"
      // branch above); adding timeoutSeconds again here would double-count
      // it, the same defect this fix closes in computeTimerFormulas (utils.ts).
      const globalSeconds =
        currentEffectiveSeconds + currentIneffectiveSeconds + currentVarSeconds;

      // Update refs for mode switch calculations
      currentEffectiveRef.current = currentEffectiveSeconds;
      currentIneffectiveRef.current = currentIneffectiveSeconds;
      currentVarRef.current = currentVarSeconds;
      clockSnapshotRef.current = {
        effectiveSeconds: currentEffectiveSeconds,
        ineffectiveSeconds: currentIneffectiveSeconds,
        varSeconds: currentVarSeconds,
      };

      // Update Displays
      setGlobalClock(formatMatchClock(globalSeconds));
      setEffectiveTime(currentEffectiveSeconds);
      setEffectiveClock(formatMatchClock(currentEffectiveSeconds));
      setIneffectiveClock(formatMatchClock(currentIneffectiveSeconds));
    };

    const interval = setInterval(updateTimers, 100);
    updateTimers(); // Initial call

    return () => clearInterval(interval);
  }, [
    match,
    clockMode,
    accumulatedIneffectiveTime,
    isVarActive,
    varStartMs,
    varPauseStartMs,
    varPausedSeconds,
    timeoutSeconds,
    isTimeoutActive,
  ]);

  // Handlers
  const handleGlobalClockStart = async () => {
    if (!match) return;
    try {
      const startStatus =
        match.status === "Pending" || match.status === "Scheduled"
          ? "Live_First_Half"
          : undefined;
      await updateMatchStatus(match.id, startStatus, "start");
      fetchMatch();
    } catch (error) {
      console.error("Failed to start global clock:", error);
    }
  };

  const handleGlobalClockStop = async () => {
    if (!match) return;
    // Cleared at the start of every attempt, same contract as
    // `modeSwitchError` — a stale failure from a previous press must not
    // keep showing after this one succeeds.
    setClockActionError(null);
    try {
      // Capture current times before network call to avoid delay
      const currentTime = monotonicClockValue(
        currentEffectiveRef.current,
        match.match_time_seconds,
      );
      // Include any in-progress VAR seconds so the stop PATCH does not
      // clobber the carry-over written by handleModeSwitch("INEFFECTIVE") on
      // the Detener-while-VAR path.  Both handleModeSwitch and handleGlobalClockStop
      // fire concurrently (neither is awaited in handleGlobalClockStopGuarded),
      // and handleGlobalClockStop's secondary updateMatch fires last — always
      // winning the race.  Without the carry-over here, the stop PATCH overwrites
      // ineffective_time_seconds with the stale pre-VAR value, dropping E seconds.
      // On a normal stop (no VAR), currentVarRef.current === 0 — no behaviour change.
      const currentIneffective = monotonicClockValue(
        currentIneffectiveRef.current + currentVarRef.current,
        match.ineffective_time_seconds,
      );
      await updateMatchStatus(match.id, undefined, "stop", currentTime);
      // Persist ineffective time so it survives stop/start cycles.
      //
      // PMA-COCK-003 v1.179.0: this second PATCH targets the same
      // `/clock-mode` endpoint `handleModeSwitch` uses, which the backend
      // now refuses with a 409 unconditionally while the match is in
      // `Penalties` (PMA-MATCH-030 v1.9.3) — the server-side guard PMA-COCK-197
      // flagged as an open gap. That refusal is expected and must stay a
      // refusal (do not special-case or suppress it here); the bug this
      // fixes was that the throw was swallowed by a console.error-only catch
      // — exactly the pattern named in this hook's own PMA-COCK-155 history
      // as the mechanism behind the 237s production time loss — which also
      // skipped the `fetchMatch()` below, leaving the operator looking at
      // un-refreshed cockpit state with no indication anything happened.
      if (currentIneffective > 0) {
        await updateMatch(match.id, {
          ineffective_time_seconds: currentIneffective,
        });
      }
    } catch (error) {
      console.error("Failed to stop global clock:", error);
      // v1.182.0: only the deliberate 409 refusal is surfaced to the
      // operator as a toast — a generic/transient error is still logged
      // above (unchanged from pre-v1.179.0 behavior) but must not raise one.
      if (isClockActionRefusal(error)) {
        setClockActionError(
          error instanceof Error
            ? error.message
            : "Failed to stop global clock",
        );
      }
    } finally {
      // Always resync from the server — success or refusal — so the
      // operator never ends this handler looking at a client state the
      // server does not share. See PMA-COCK-003 v1.179.0.
      fetchMatch();
    }
  };

  const handleGlobalClockReset = async () => {
    if (!match) return;
    setClockActionError(null);
    try {
      // Attempt to reset status/clocks via status endpoint; backend may reject Fulltime→Pending
      try {
        if (match.status === "Completed" || match.status === "Fulltime") {
          await updateMatchStatus(match.id, undefined, "reset", 0);
        } else {
          await updateMatchStatus(match.id, "Pending", "reset", 0);
        }
      } catch (statusError) {
        console.warn(
          "[Reset] Status reset rejected, continuing with clock reset",
          statusError,
        );
      }

      // PMA-COCK-003 v1.179.0: this PATCH targets `/clock-mode` directly —
      // same 409-while-Penalties refusal as handleGlobalClockStop's second
      // call above, same reason it must not be suppressed here. See that
      // handler's comment for the full mechanism.
      await updateMatch(match.id, {
        // Explicit opt-out of the monotonic guard on both sides. Without it
        // the server clamps these zeros back to the stored clock and "reset
        // match" silently stops working.
        allow_clock_rewind: true,
        match_time_seconds: 0,
        clock_seconds_at_period_start: 0,
        ineffective_time_seconds: 0,
        current_period_start_timestamp: null,
        last_mode_change_timestamp: null,
        period_timestamps: {},
        clock_mode: "EFFECTIVE",
      });
    } catch (error) {
      console.error("Failed to reset global clock:", error);
      // v1.182.0: same narrowing as handleGlobalClockStop above — only the
      // deliberate 409 refusal is surfaced as a toast.
      if (isClockActionRefusal(error)) {
        setClockActionError(
          error instanceof Error
            ? error.message
            : "Failed to reset global clock",
        );
      }
    } finally {
      fetchMatch();
    }
  };

  const handleModeSwitch = async (
    newMode: "EFFECTIVE" | "INEFFECTIVE",
  ): Promise<boolean> => {
    if (!match) return false;

    const previousMode = clockMode;

    // Optimistic update
    setClockMode(newMode);
    setModeSwitchError(null);

    // Every mode-switch PATCH is self-contained: it always resends all
    // three clock accumulators, computed from the live refs, regardless of
    // which mode is being left. This makes any single successful PATCH
    // fully repair a previously dropped one — see PMA-COCK-155, where a
    // permanently-dropped EFFECTIVE→INEFFECTIVE PATCH lost 237s in prod
    // because the following resume PATCH omitted them on the (wrong)
    // assumption the dropped PATCH had already banked them server-side.
    // All three fields are monotonic-guarded server-side
    // (matches_new.MONOTONIC_CLOCK_FIELDS), so resending known-good values
    // can never rewind the stored clock.
    const nextEffective = monotonicClockValue(
      currentEffectiveRef.current,
      match.clock_seconds_at_period_start ?? match.match_time_seconds,
    );
    let nextIneffective = monotonicClockValue(
      currentIneffectiveRef.current,
      match.ineffective_time_seconds,
    );
    if (previousMode === "EFFECTIVE") {
      // Carry any VAR seconds into ineffective_time_seconds so the global
      // clock does not snap back after VARStop. During VAR, currentVarSeconds
      // contributes to globalSeconds but is not persisted anywhere. Without
      // this carry-over, after fetchMatch the accumulated VAR duration would
      // be lost and the global clock would drop by ~E seconds.
      const varCarryOver = Math.round(currentVarRef.current);
      if (varCarryOver > 0) {
        nextIneffective = monotonicClockValue(
          currentIneffectiveRef.current + varCarryOver,
          match.ineffective_time_seconds,
        );
      }
    }

    const updates: Record<string, any> = {
      clock_mode: newMode,
      last_mode_change_timestamp: new Date().toISOString(),
      current_period_start_timestamp: new Date().toISOString(),
      match_time_seconds: nextEffective,
      clock_seconds_at_period_start: nextEffective,
      ineffective_time_seconds: nextIneffective,
    };

    try {
      let lastError: unknown;
      let succeeded = false;
      for (let attempt = 1; attempt <= MODE_SWITCH_MAX_ATTEMPTS; attempt++) {
        try {
          await updateMatch(match.id, updates);
          succeeded = true;
          break;
        } catch (err) {
          lastError = err;
          if (attempt < MODE_SWITCH_MAX_ATTEMPTS) {
            await wait(MODE_SWITCH_RETRY_BASE_MS * attempt);
          }
        }
      }
      if (!succeeded) throw lastError;
      fetchMatch();
      return true;
    } catch (error) {
      console.error("Failed to switch clock mode:", error);
      // The server never accepted this mode — do not leave the operator
      // looking at a mode/clock the server does not have. Revert the
      // optimistic local mode and re-sync from the server so the client
      // never silently diverges (the prod incident's actual mechanism: a
      // console.error-only catch left clockMode flipped forever against a
      // server that still had the old mode and the old clock baseline).
      setClockMode(previousMode);
      setModeSwitchError(
        error instanceof Error ? error.message : "Failed to switch clock mode",
      );
      fetchMatch();
      return false;
    }
  };

  return {
    globalClock,
    effectiveClock,
    effectiveTime,
    ineffectiveClock,
    clockMode,
    isClockRunning,
    clockSnapshotRef,
    modeSwitchError,
    clockActionError,
    handleGlobalClockStart,
    handleGlobalClockStop,
    handleGlobalClockReset,
    handleModeSwitch,
  };
};
